import { useState } from 'react';
import { TECHNOLOGIES } from '~/containers/Sims/BulkAction/TechnologiesInput';
import {
  ACTIVE_STATUS_ITEMS,
  FILTERS_DATA,
  ONLINE_STATUS_ITEMS,
  SIM_TYPE_ITEMS,
  TAG_TYPE_ITEMS,
} from '~/containers/Sims/constants';
import useTagsSearch from '~/hooks/use-tags-search';
import useAllNetworks from './use-all-networks';
import useAuthentication from './use-authentication';
import useMccs from './use-mccs';
import useTiering from './use-tiering';

function convertCountriesToItems(countries) {
  if (!countries) return [];
  const countryItems = countries.map(({ countryCode, country }) => ({
    label: `${country} (${countryCode})`,
    value: countryCode,
  }));
  return [{ label: 'None', value: 'none' }, ...countryItems];
}

function convertNetworksToItems(networks) {
  if (!networks) return [];
  const networkItems = [...new Map(networks.map(({ name }) => [name, { label: name, value: name }])).values()];
  const sortedNetworkItems = networkItems.sort((a, b) => a.label.localeCompare(b.label));
  return [{ label: 'None', value: 'none' }, ...sortedNetworkItems];
}

function formatInitialFilters(initialFilter) {
  if (!initialFilter) return [];
  return initialFilter.map(f => {
    const filter = FILTERS_DATA[f.key];
    const type = filter?.type;
    return { ...f, type };
  });
}

function useSearchSimsFilters(organizationId, initialFilter, { resetPages }) {
  const { isGod, isDemiGod, isOrgMember } = useAuthentication();
  const isGodly = isGod || isDemiGod;

  const tagsSearch = useTagsSearch('tags', organizationId, true);
  const connectorsSearch = useTagsSearch('connectors', organizationId, true);
  const networkListsSearch = useTagsSearch('network-lists', organizationId, true);
  const tiering = useTiering();
  const [activeFilters, setActiveFilters] = useState(formatInitialFilters(initialFilter));
  const { mccs, loading: mccsLoading } = useMccs();
  const mccsWithNoneOption = [{ label: 'None', value: 'none' }, ...mccs];
  const allNetworks = useAllNetworks();

  const countriesItems = convertCountriesToItems(allNetworks.data?.countries);
  const networkItems = convertNetworksToItems(allNetworks.data?.networks);

  function clearFilters() {
    if (!activeFilters.length) return;
    setActiveFilters([]);
    resetPages();
  }

  function addFilters(key, values, removeOldValue = false) {
    setActiveFilters(prev => {
      const valueSet = new Set(values);

      let next = [...prev];

      next = removeOldValue
        ? next.filter(f => f.key !== key)
        : next.filter(f => f.key !== key || !valueSet.has(f.value));
      const filter = FILTERS_DATA[key];
      const type = filter?.type;

      return [...next, ...values.map(value => ({ key, value, type }))];
    });

    resetPages();
  }

  function setItems(key, _items, useLabelAsValue = false) {
    const items = Array.isArray(_items) ? _items : [_items];
    const values = items.map(item => (useLabelAsValue ? item.label : item.value));
    const valueSet = new Set(values);

    setActiveFilters(prev => {
      const kept = prev.filter(f => f.key !== key || valueSet.has(f.value));
      const existingValues = new Set(prev.filter(f => f.key === key).map(f => f.value));
      const added = values
        .filter(v => !existingValues.has(v))
        .map(v => {
          const filter = FILTERS_DATA[key];
          const type = filter?.type;
          return { key, value: v, type };
        });
      return [...kept, ...added];
    });

    resetPages();
  }

  function replaceValue(key, value) {
    const values = value ? [value] : [];
    addFilters(key, values, true);
  }

  function getFilterValue(key, csv = false) {
    const filter = activeFilters.find(filter => filter.key === key);
    if (!filter) return undefined;
    if (csv && filter.value) {
      return filter.value.split(',');
    }
    return filter.value;
  }

  function getValue(key, items) {
    if (!items.length) return;
    const currentFilter = activeFilters.find(filter => filter.key === key);
    if (!currentFilter) return;
    const filterItem = items.find(item => item.value === currentFilter.value || item.label === currentFilter.value);
    if (!filterItem) return;
    return filterItem.value;
  }

  function getValues(key, items) {
    if (!items.length) return;
    const currentFilters = activeFilters.filter(filter => filter.key === key);
    if (!currentFilters.length) return;
    return currentFilters.map(filter => {
      const filterItem = items.find(item => item.label === filter.value);
      return filterItem ? filterItem.value : filter.value;
    });
  }

  const connectorSelector = {
    key: 'connector',
    ...FILTERS_DATA.connector,
    defaultSelected: getValue('connector', connectorsSearch.items),
    onSelectedChange: item => replaceValue('connector', item?.label),
    ...connectorsSearch,
  };

  const tagPermissionsFilter = {
    key: 'tagTypes',
    ...FILTERS_DATA.tagTypes,
    defaultSelected: getValues('tagTypes', TAG_TYPE_ITEMS),
    onSelectedChange: items => setItems('tagTypes', items),
  };

  const filters = {
    activeFilters,
    clear: clearFilters,
    filters: [
      {
        key: 'online',
        ...FILTERS_DATA.online,
        defaultSelected: getValue('online', ONLINE_STATUS_ITEMS),
        onSelectedChange: item => replaceValue('online', item?.value),
      },
      {
        key: 'simId',
        ...FILTERS_DATA.simId,
        value: getFilterValue('simId', true),
        clear: () => replaceValue('simId', ''),
        onChange: values => replaceValue('simId', values.join(',')),
      },
      {
        key: 'label',
        ...FILTERS_DATA.label,
        value: getFilterValue('label', true),
        clear: () => replaceValue('label', ''),
        onChange: values => replaceValue('label', values.join(',')),
      },
      {
        key: 'ipv4',
        ...FILTERS_DATA.ipv4,
        value: getFilterValue('ipv4', true),
        clear: () => replaceValue('ipv4', ''),
        onChange: values => replaceValue('ipv4', values.join(',')),
      },
      {
        key: 'iccid',
        ...FILTERS_DATA.iccid,
        value: getFilterValue('iccid', true),
        clear: () => replaceValue('iccid', ''),
        onChange: values => replaceValue('iccid', values.join(',')),
      },
      {
        key: 'activated',
        ...FILTERS_DATA.activated,
        defaultSelected: getValue('activated', ACTIVE_STATUS_ITEMS),
        onSelectedChange: item => replaceValue('activated', item?.value),
      },
      {
        key: 'tags',
        ...tagsSearch,
        ...FILTERS_DATA.tags,
        defaultSelected: getValues('tags', tagsSearch.items),
        onSelectedChange: items => setItems('tags', items, true),
      },
      {
        key: 'networkWhitelist',
        ...networkListsSearch,
        ...FILTERS_DATA.networkWhitelist,
        defaultSelected: getValue('networkWhitelist', networkListsSearch.items),
        onSelectedChange: item => replaceValue('networkWhitelist', item?.label),
      },
      {
        key: 'type',
        ...FILTERS_DATA.type,
        defaultSelected: getValue('type', SIM_TYPE_ITEMS),
        onSelectedChange: item => replaceValue('type', item?.value),
      },
      {
        key: 'mcc',
        ...FILTERS_DATA.mcc,
        defaultSelected: getValue('mcc', mccsWithNoneOption),
        onSelectedChange: item => replaceValue('mcc', item?.value),
        items: mccsWithNoneOption,
        loading: mccsLoading,
      },
      {
        key: 'countryCode',
        ...FILTERS_DATA.countryCode,
        defaultSelected: getValue('countryCode', countriesItems),
        onSelectedChange: item => replaceValue('countryCode', item?.value),
        items: countriesItems,
      },
      {
        key: 'networkName',
        ...FILTERS_DATA.networkName,
        defaultSelected: getValue('networkName', networkItems),
        onSelectedChange: item => replaceValue('networkName', item?.value),
        items: networkItems,
      },
      {
        key: 'technologies',
        ...FILTERS_DATA.technologies,
        defaultSelected: getValues('technologies', TECHNOLOGIES),
        onSelectedChange: items => setItems('technologies', items),
      },
      {
        key: 'imsi',
        ...FILTERS_DATA.imsi,
        value: getFilterValue('imsi', true),
        clear: () => replaceValue('imsi', ''),
        onChange: values => replaceValue('imsi', values.join(',')),
      },
    ],
  };

  if (isOrgMember || isGodly) filters.filters.push(tagPermissionsFilter);
  if (tiering.hasFeature('connectors')) filters.filters.splice(2, 0, connectorSelector);

  return filters;
}

export default useSearchSimsFilters;
